import React, { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';

import { useNavigation } from '../../../../shared/utils/useNavigation';
import ModernHeader from '../../../../components/layout/Headers/ModernHeader';
import VatInfoModal from '../../../Modals/EarningsPayout/VatInfoModal';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControlWithFocus,
  FormMessageWithValidation,
  useFormValidation,
} from '../../../../shared/components/form';
import { createVatNumberSchema } from '../../../../shared/validations/transaction';
import { getVatRateByCountryCode } from '../../../../shared/constants/vatRates';
import { fetchCreator, updateCreator } from '../../../../shared/actions/creatorActions';
import { showSuccess } from '../../../../shared/utils/swalHandler';

const VatNumber = ({
  creator: creatorInfo,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const [valueAddedTaxModal, setValueAddedTaxModal] = useState(false);

  // Pre-fill country from ID verification (user_data.country) when vat_country not yet set
  const initialCountry =
    creatorInfo?.user_data?.vat_country || creatorInfo?.user_data?.country || '';

  const form = useForm({
    resolver: zodResolver(createVatNumberSchema()),
    defaultValues: {
      vat_country: initialCountry,
      vat_number: creatorInfo?.user_data?.vat_number || '',
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Reset form values when creatorInfo changes (e.g., after async load)
  useEffect(() => {
    if (creatorInfo?.user_data) {
      const country =
        creatorInfo.user_data.vat_country || creatorInfo.user_data.country || '';
      form.reset({
        vat_country: country,
        vat_number: creatorInfo.user_data.vat_number || '',
      });
    }
  }, [creatorInfo?.user_data, form]);

  const { validateAndFocus } = useFormValidation(form, {
    showErrorsOnSubmit: true,
    focusOnFirstError: true,
  });

  const onSubmit = useCallback(
    async (values) => {
      const isValid = await validateAndFocus();
      if (!isValid) return;

      try {
        const result = await updateCreator({
          vat_country: values.vat_country,
          vat_number: values.vat_number,
        });

        if (result.success) {
          showSuccess('Success', 'VAT number updated successfully');

          if (creatorInfo?.user_data?.username) {
            await dispatch(fetchCreator(creatorInfo.user_data.username));
          }
        } else {
          toast.error(result.error || result.message || 'Failed to update VAT number');
        }
      } catch (error) {
        toast.error(error.message || 'An unexpected error occurred');
      }
    },
    [validateAndFocus, dispatch, creatorInfo?.user_data?.username]
  );

  return (
    <div className="modern-earnings-payout-page">
      <ModernHeader
        customTitle="VAT Number"
        showButtonBorder={false}
        hideSearchIcon
        showAvatar={false}
        showBackButton
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <div className='vat_top_sec'>
            <span><i className='fa-solid fa-receipt'></i></span>
            <p>
              To collect VAT we need to verify that you are registered. Please enter your VAT number below.
            </p>
          </div>

          <div className='cm_dark_form mb-4'>
            <FormField
              control={form.control}
              name="vat_country"
              render={({ field }) => (
                <FormItem className='form-group'>
                  <FormLabel className='text-white'>Country</FormLabel>
                  <FormControlWithFocus fieldName="vat_country">
                    <div className='with_down_angle'>
                      <select
                        className='form-control cm_form_control'
                        disabled={creatorInfo?.user_data?.vat_country ? true : false}
                        {...field}
                      >
                        <option value="">Select Country</option>
                        {getVatRateByCountryCode.map(({ code, name }) => (
                          <option key={code} value={code}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </FormControlWithFocus>
                  <FormMessageWithValidation
                    fieldName="vat_country"
                    useValidation
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vat_number"
              render={({ field }) => (
                <FormItem className='form-group'>
                  <FormLabel className='text-white'>VAT Number</FormLabel>
                  <FormControlWithFocus fieldName="vat_number">
                    <div className='cm_with_icon'>
                      <input
                        type='text'
                        className='form-control cm_form_control'
                        placeholder='Enter VAT number'
                        disabled={creatorInfo?.user_data?.vat_number ? true : false}
                        {...field}
                      />
                      <span
                        className='modal_btn with_pink_hover'
                        onClick={() => setValueAddedTaxModal(true)}
                      >
                        <i className='fa-solid fa-circle-question'></i>
                      </span>
                    </div>
                  </FormControlWithFocus>
                  <FormMessageWithValidation
                    fieldName="vat_number"
                    useValidation
                  />
                </FormItem>
              )}
            />
          </div>

          <h2>Why Provide a VAT Number?</h2>

          <div className='vat_card'>
            <div>
              <span><i className='fa-solid fa-shield-halved'></i></span>
            </div>
            <div className='w-full'>
              <h4>Compliance</h4>
              <p>Ensure you are compliant with tax regulations.</p>
            </div>
          </div>

          <div className='vat_card'>
            <div>
              <span><i className='fa-solid fa-receipt'></i></span>
            </div>
            <div className='w-full'>
              <h4>VAT Collection</h4>
              <p>
                Enter your VAT number for Juicy House to collect the appropriate tax at checkout. For any sales
                made while your VAT number was missing you are responsible for reviewing your transaction history,
                calculating the VAT that should have been applied, and including it in your next VAT return.
              </p>
              <p>
                You can export earnings reports for the selected year by tapping Export Data at the bottom of the
                Earnings & Payouts screen.
              </p>
            </div>
          </div>

          <div className='vat_card'>
            <div>
              <span><i className='fa-solid fa-circle-question'></i></span>
            </div>
            <div className='w-full'>
              <h4>Country Not Listed?</h4>
              <p>
                If your country is not listed, VAT may not apply to our services there. You are still responsible
                for following any local tax requirements. To help with your records, you can export your earnings
                by scrolling to the bottom of the Earnings & Payouts page, selecting Export Data, and then choosing
                the year you wish to download.
              </p>
            </div>
          </div>

          <div className='need_help_card'>
            <span><i className='fa-solid fa-headset'></i></span>
            <h3>Need Help?</h3>
            <p>Our support team is here to assist you</p>
            <button
              type="button"
              className='btn btn_primary_outline'
              onClick={() => navigate.toContactSupport()}
            >Contact Support</button>
          </div>

          {!creatorInfo?.user_data?.vat_number && (
            <>
              <hr className='cm_hr' />
              <button
                type="submit"
                className='btn btn_primary rounded_8 w-full'
                disabled={creatorInfo?.user_data?.vat_number || form.formState.isSubmitting || !form.formState.isValid}
              >
                Submit VAT Number
              </button>
            </>
          )}
        </form>
      </Form>

      <VatInfoModal
        isOpen={valueAddedTaxModal}
        toggle={() => setValueAddedTaxModal(false)}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    creator: state.creator?.creator,
  };
};

export default connect(mapStateToProps)(VatNumber);
